import { useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useSignUp } from '@clerk/clerk-react'
import { getSafeRedirectPath } from '@/lib/redirect'
import { ROUTES } from '@/routes/routes'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { AuthLayout } from '@/components/auth/AuthLayout'
import { GoogleIcon } from '@/components/auth/GoogleIcon'
import { markPendingSignInMethod } from '@/lib/pendingSignInMethod'
import { clerkErrorMessage } from '@/lib/clerkError'
import { completeAuthAndRedirect } from '@/lib/postAuthRedirect'

export default function SignUpPage() {
  const { isLoaded, signUp, setActive } = useSignUp()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const redirectTo = getSafeRedirectPath(searchParams.get('redirectTo'))

  const [step, setStep] = useState<'details' | 'verify' | 'finish-google'>('details')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Lands here via continueSignUpUrl (SSOCallbackPage) when Google didn't supply a name and
  // "Name" is a required field on the Clerk instance. Deliberately narrow: only when name is
  // THE ONLY thing missing — a cancelled/abandoned Google attempt also lands here via
  // continueSignUpUrl with a mostly-empty signUp resource (missing email/external account/etc
  // too), and that should just fall through to the normal manual sign-up form, not this screen.
  // Adjusted during render (React's documented pattern for syncing external state into local
  // state), not in an effect — appliedGooglePrefill makes it apply once: signUp gets a fresh
  // resource reference on every mutating call, and reapplying after that would stomp on
  // whatever the user has since typed.
  const [appliedGooglePrefill, setAppliedGooglePrefill] = useState(false)
  const missingFields = signUp?.missingFields ?? []
  const onlyMissingName =
    missingFields.length > 0 && missingFields.every((f) => f === 'first_name' || f === 'last_name')
  if (!appliedGooglePrefill && onlyMissingName) {
    setAppliedGooglePrefill(true)
    setFirstName(signUp?.firstName ?? '')
    setLastName(signUp?.lastName ?? '')
    setStep('finish-google')
  }

  async function onFinishGoogleSignUp(e: React.FormEvent) {
    e.preventDefault()
    if (!isLoaded || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const result = await signUp.update({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      })
      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId })
        await completeAuthAndRedirect(navigate, redirectTo, 'google')
      } else {
        setError('Unable to finish signing up. Please try again.')
      }
    } catch (err) {
      setError(clerkErrorMessage(err, 'Unable to finish signing up. Please try again.'))
    } finally {
      setSubmitting(false)
    }
  }

  async function onGoogleSignUp() {
    if (!isLoaded || submitting) return
    setError(null)
    try {
      markPendingSignInMethod('google')
      await signUp.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: ROUTES.AUTH.SSO_CALLBACK,
        redirectUrlComplete: redirectTo,
      })
    } catch (err) {
      setError(clerkErrorMessage(err, 'Unable to continue with Google. Please try again.'))
    }
  }

  async function onSubmitDetails(e: React.FormEvent) {
    e.preventDefault()
    if (!isLoaded || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await signUp.create({
        emailAddress: email,
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      })
      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' })
      setStep('verify')
    } catch (err) {
      setError(clerkErrorMessage(err, 'Unable to create your account. Please try again.'))
    } finally {
      setSubmitting(false)
    }
  }

  async function onSubmitCode(e: React.FormEvent) {
    e.preventDefault()
    if (!isLoaded || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const result = await signUp.attemptEmailAddressVerification({ code })
      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId })
        await completeAuthAndRedirect(navigate, redirectTo, 'email_password')
      } else {
        setError('That code didn’t complete sign-up. Please try again.')
      }
    } catch (err) {
      setError(clerkErrorMessage(err, 'Invalid or expired code. Please try again.'))
      setCode('')
    } finally {
      setSubmitting(false)
    }
  }

  async function resendCode() {
    if (!isLoaded) return
    try {
      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' })
      setError(null)
    } catch (err) {
      setError(clerkErrorMessage(err, 'Failed to resend the code. Please try again.'))
    }
  }

  if (step === 'finish-google') {
    return (
      <AuthLayout
        title="Almost there"
        subtitle="Google didn't share your name with us — add it to finish creating your account."
      >
        <form onSubmit={onFinishGoogleSignUp} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label
                htmlFor="finishFirstName"
                className="text-sm font-medium text-[var(--fl-heading)]"
              >
                First name
              </label>
              <Input
                id="finishFirstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoFocus
                autoComplete="given-name"
                required
              />
            </div>

            <div className="space-y-1">
              <label
                htmlFor="finishLastName"
                className="text-sm font-medium text-[var(--fl-heading)]"
              >
                Last name
              </label>
              <Input
                id="finishLastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
                required
              />
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-[var(--fl-error-bg)] px-4 py-3 text-sm text-[var(--fl-error)]">
              {error}
            </div>
          )}

          <Button type="submit" disabled={!isLoaded || submitting} className="w-full">
            {submitting ? 'Finishing…' : 'Continue'}
          </Button>
        </form>
      </AuthLayout>
    )
  }

  if (step === 'verify') {
    return (
      <AuthLayout title="Check your email" subtitle={`Enter the 6-digit code we sent to ${email}.`}>
        <form onSubmit={onSubmitCode} className="space-y-4">
          <Input
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            autoComplete="one-time-code"
            autoFocus
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="text-center font-mono text-2xl tracking-widest text-[var(--fl-heading)]"
            aria-label="6-digit verification code"
          />

          {error && (
            <div className="rounded-md bg-[var(--fl-error-bg)] px-4 py-3 text-sm text-[var(--fl-error)]">
              {error}
            </div>
          )}

          <Button type="submit" disabled={code.length !== 6 || submitting} className="w-full">
            {submitting ? 'Verifying…' : 'Verify'}
          </Button>
        </form>

        <p className="text-xs text-[var(--fl-text-3)] text-center">
          Didn't get it?{' '}
          <button
            type="button"
            onClick={resendCode}
            className="text-[var(--fl-primary)] hover:underline"
          >
            Resend code
          </button>
        </p>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="Create an account" subtitle="Start tracking your household's spending.">
      <Button
        type="button"
        variant="outline"
        onClick={onGoogleSignUp}
        disabled={!isLoaded || submitting}
        className="w-full"
      >
        <GoogleIcon className="h-4 w-4" />
        Continue with Google
      </Button>

      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-[var(--fl-border)]" />
        <span className="text-xs text-[var(--fl-text-3)]">or</span>
        <div className="h-px flex-1 bg-[var(--fl-border)]" />
      </div>

      <form onSubmit={onSubmitDetails} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label htmlFor="firstName" className="text-sm font-medium text-[var(--fl-heading)]">
              First name
            </label>
            <Input
              id="firstName"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoFocus
              autoComplete="given-name"
              required
            />
          </div>

          <div className="space-y-1">
            <label htmlFor="lastName" className="text-sm font-medium text-[var(--fl-heading)]">
              Last name
            </label>
            <Input
              id="lastName"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
              required
            />
          </div>
        </div>

        <div className="space-y-1">
          <label htmlFor="email" className="text-sm font-medium text-[var(--fl-heading)]">
            Email
          </label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="password" className="text-sm font-medium text-[var(--fl-heading)]">
            Password
          </label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
          />
        </div>

        {error && (
          <div className="rounded-md bg-[var(--fl-error-bg)] px-4 py-3 text-sm text-[var(--fl-error)]">
            {error}
          </div>
        )}

        <Button type="submit" disabled={!isLoaded || submitting} className="w-full">
          {submitting ? 'Creating account…' : 'Continue'}
        </Button>
      </form>

      <p className="text-xs text-[var(--fl-text-3)] text-center">
        Already have an account?{' '}
        <Link
          to={`${ROUTES.AUTH.SIGN_IN}?redirectTo=${encodeURIComponent(redirectTo)}`}
          className="text-[var(--fl-primary)] hover:underline"
        >
          Sign in
        </Link>
      </p>
    </AuthLayout>
  )
}
